"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth, useUser } from "@clerk/nextjs";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import type { CaptureResult } from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import type { ReactNode } from "react";
import { Suspense, useEffect, useRef } from "react";
import { usePostHogConsent } from "@/hooks/use-posthog-consent";
import { POSTHOG_BROWSER_PROXY_HOST, applyPostHogDebugQueryParam, getPostHogAppHost, getPostHogProjectKey, hasPostHogConsent, identifyPosthogUser, shouldFilterPostHogException } from "@/lib/posthog";
import { isPrivateStatusUrl } from "@/lib/private-status-route";
const POSTHOG_KEY = getPostHogProjectKey();
const POSTHOG_PII_DENYLIST = ["email", "name", "firstName", "lastName", "phone", "document", "cpf", "cnpj", "address", "image"];
function getEventProperty(event: unknown, key: string) {
  if (!event || typeof event !== "object" || !("properties" in event)) {
    return undefined;
  }
  const properties = event.properties;
  if (!properties || typeof properties !== "object" || !(key in properties)) {
    return undefined;
  }
  return properties[key as keyof typeof properties];
}
function beforeSendPostHogEvent(event: CaptureResult | null) {
  if (!event) {
    return event;
  }
  if (typeof window !== "undefined" && isPrivateStatusUrl(window.location.pathname)) return null;
  for (const key of ["$current_url", "$referrer"]) {
    const value = getEventProperty(event, key);
    if (typeof value === "string" && isPrivateStatusUrl(value)) return null;
  }
  const eventName = event && typeof event === "object" && "event" in event ? event.event : undefined;
  if (eventName !== "$exception") {
    return event;
  }
  const message = getEventProperty(event, "$exception_message");
  if (typeof message === "string" && shouldFilterPostHogException(message)) {
    return null;
  }
  return event;
}
function PostHogInit() {
  const $ = _c(14);
  const initRef = useRef(false);
  const consent = usePostHogConsent();
  const pathname = usePathname();
  const t0 = pathname ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = isPrivateStatusUrl(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const privateStatus = t1;
  const consented = consent === "accepted" && !privateStatus;
  let t2;
  let t3;
  if ($[2] !== privateStatus) {
    t2 = () => {
      if (privateStatus && window.__posthogInitialized) {
        posthog.stopSessionRecording();
      }
    };
    t3 = [privateStatus];
    $[2] = privateStatus;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== consented || $[6] !== privateStatus) {
    t4 = () => {
      if (!POSTHOG_KEY || privateStatus) {
        return;
      }
      if (consented) {
        posthog.opt_in_capturing();
      } else {
        posthog.opt_out_capturing();
        posthog.reset();
      }
    };
    t5 = [consented, privateStatus];
    $[5] = consented;
    $[6] = privateStatus;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== consented) {
    t6 = () => {
      if (!POSTHOG_KEY || !consented || initRef.current) {
        return;
      }
      initRef.current = true;
      applyPostHogDebugQueryParam();
      const posthogConfig = {
        api_host: POSTHOG_BROWSER_PROXY_HOST,
        ui_host: getPostHogAppHost(),
        capture_pageview: false,
        capture_pageleave: true,
        capture_exceptions: true,
        enable_recording_console_log: false,
        property_denylist: POSTHOG_PII_DENYLIST,
        before_send: beforeSendPostHogEvent,
        loaded: _temp
      } satisfies Parameters<typeof posthog.init>[1] & {
        enable_recording_console_log?: boolean;
        property_denylist?: string[];
      };
      posthog.init(POSTHOG_KEY, posthogConfig);
    };
    $[9] = consented;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== consented || $[12] !== privateStatus) {
    t7 = [consented, privateStatus];
    $[11] = consented;
    $[12] = privateStatus;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  useEffect(t6, t7);
  return null;
}
function _temp() {
  window.__posthogInitialized = true;
  if (isPrivateStatusUrl(window.location.pathname)) {
    posthog.stopSessionRecording();
  }
}
function PostHogPageView() {
  const $ = _c(4);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t0 = () => {
      if (!POSTHOG_KEY || !pathname || !hasPostHogConsent()) {
        return;
      }
      const url = searchParams.size > 0 ? `${pathname}?${searchParams.toString()}` : pathname;
      posthog.capture("$pageview", {
        $current_url: url,
        source: "browser"
      });
    };
    t1 = [pathname, searchParams];
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return null;
}
function PostHogIdentify() {
  const $ = _c(4);
  const {
    isSignedIn
  } = useAuth();
  const {
    user
  } = useUser();
  let t0;
  let t1;
  if ($[0] !== isSignedIn || $[1] !== user) {
    t0 = () => {
      if (!POSTHOG_KEY || !hasPostHogConsent()) {
        return;
      }
      if (isSignedIn && user) {
        identifyPosthogUser(user.id);
      } else {
        if (isSignedIn === false) {
          posthog.reset();
        }
      }
    };
    t1 = [isSignedIn, user];
    $[0] = isSignedIn;
    $[1] = user;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return null;
}
export function PostHogProvider(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  if (!POSTHOG_KEY) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PostHogInit />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Suspense><PostHogPageView /></Suspense>;
    t3 = <PostHogIdentify />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children) {
    t4 = <PHProvider client={posthog}>{t1}{t2}{t3}{children}</PHProvider>;
    $[5] = children;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}